import { cn } from '@/lib/utils';

import { MenuCarousel, MenuCarouselProps } from './MenuCarousel';
import { MenuItemProps } from './MenuItem';
import { MenuList } from './MenuList';
import { MenuListWrapper } from './MenuListWrapper';
import { MenuPromoBannerProps } from './MenuPromoBanner';

export type MenuGroupProps = {
  type?: 'main' | 'extra';
  lists: {
    title: string;
    link?: MenuItemProps;
    items: MenuItemProps[];
    hideTitleOnMobile?: boolean;
    hideSectionOnMobile?: boolean;
  }[];
  promoBanners?: MenuPromoBannerProps[];
  carousel?: MenuCarouselProps & { title: string };
  onLinkClick?: (
    params: { title: string } | { url: string; keepMenuOpen?: boolean }
  ) => void;
};

export function MenuGroup({
  type,
  lists,
  carousel,
  onLinkClick,
}: MenuGroupProps) {
  const nonEmptyLists = lists.filter((list) => list.items.length > 0);

  const hasNonEmptyLists = nonEmptyLists.length > 0;
  const hasCarousel = !!carousel && carousel.items.length > 0;

  const hasGroupItem = hasNonEmptyLists || hasCarousel;
  const groupType = type ?? 'main';

  if (!hasGroupItem) return null;

  return (
    <section
      className={cn(
        'group/menu-group headerLg:pt-6 headerLg:pb-6 headerLg:pr-6 headerLg:pl-2 headerLg:flex-row flex h-full flex-col gap-y-4 px-3',
        groupType === 'extra' &&
          'group/menu-group headerLg:px-6 headerLg:pr-0 headerLg:pl-6 headerLg:flex headerLg:flex-col after:bg-gray-dark-50 relative hidden w-full flex-col gap-y-10 bg-gray-50 after:absolute after:left-0 after:top-0 after:h-full after:w-screen'
      )}
      data-type={groupType}
    >
      {hasCarousel && (
        <MenuListWrapper
          title={carousel.title ?? ''}
          classNames={{ root: 'headerLg:h-auto', title: 'pl-0' }}
        >
          <MenuCarousel {...carousel} />
        </MenuListWrapper>
      )}
      {nonEmptyLists.map((list, i) => (
        <MenuListWrapper
          key={i}
          title={list.title}
          hideTitleOnMobile={list.hideTitleOnMobile}
          classNames={{
            root: cn(
              type !== 'extra' && 'h-full',
              hasCarousel && '-ml-3',
              hasCarousel && 'headerLg:ml-0',
              type === 'extra' &&
                cn(
                  hasCarousel
                    ? 'overflow-y-auto overflow-x-clip'
                    : 'overflow-x-auto overflow-y-clip'
                ),
              type !== 'extra' &&
                i < nonEmptyLists.length - 1 &&
                'headerLg:border-r headerLg:border-gray-200 headerLg:pr-8',
              type !== 'extra' && i > 0 && 'headerLg:pl-8',
              list.hideSectionOnMobile && 'hidden headerLg:block'
            ),

            title: nonEmptyLists.length < 2 ? 'hidden headerLg:block' : '',
          }}
        >
          <MenuList
            items={list.items}
            suffixItem={list.link}
            onLinkClick={onLinkClick}
          />
        </MenuListWrapper>
      ))}
    </section>
  );
}
